import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle } from "lucide-react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { useConfig } from "../lib/useConfig";


const LEARN = [
  {
    t: "Revenue versus real financial control",
    d: "Why strong top-line revenue can hide cash-flow pressure, weak reserves, receivables problems, and trucks that are not contributing what the owner assumes.",
  },
  {
    t: "The five-truck-to-ten-truck trap",
    d: "Where informal communication, owner memory, and reactive decisions begin to break down.",
  },
  {
    t: "Truck-by-truck economics",
    d: "Why company-wide averages can hide weak equipment, lanes, customers, downtime, deadhead, and maintenance decisions.",
  },
  {
    t: "The owner-dependency problem",
    d: "How the owner quietly becomes the operating system — and why adding more people or trucks does not automatically fix it.",
  },
  {
    t: "The Driver Reborn Advisory correction sequence",
    d: "What to investigate first and how to think through a practical 30-, 60-, and 90-day rebuild.",
  },
];

export default function Training() {
  const cfg = useConfig();
  const videoUrl = cfg?.training_video_url || "";
  return (
    <Layout minimal>
      <SEO title="30-Minute Fleet Clarity Training | Driver Reborn Advisory" description="Brandon Winston walks fleet owners through why a busy, growing trucking company can still lose profit, control, and freedom — and what to correct first." noindex={true} />
      <section className="section">
        <div className="container-narrow">
          <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">Brandon's 30-Minute Fleet Clarity Training</div>
          <h1 className="font-bebas text-5xl sm:text-6xl uppercase leading-tight mb-4" data-testid="training-title">
            Why a Busy, Growing Fleet<br/>
            <span className="text-[#FF6A00]">Can Still Lose Profit, Control, and Owner Freedom.</span>
          </h1>
          <p className="text-lg text-[#E6E6E6] leading-relaxed mb-4 max-w-3xl" data-testid="training-intro-1">
            Your trucks can be moving. Revenue can look healthy. Customers can be calling. And the company can still be financially fragile behind the scenes.
          </p>
          <p className="text-base text-[#A1A1AA] leading-relaxed mb-10 max-w-3xl" data-testid="training-intro-2">
            In this Fleet Clarity Training, Brandon Winston walks through the operating problems that often appear when a trucking company grows faster than its backend systems.
          </p>

          {videoUrl ? (
            <div className="aspect-video border border-[#2B2B2B] bg-[#0A0A0A] mb-10" data-testid="training-video">
              <iframe src={videoUrl} className="w-full h-full" title="Driver Reborn Advisory 30-Minute Fleet Clarity Training" allowFullScreen frameBorder="0"/>
            </div>
          ) : (
            <div className="border border-[#FF6A00] bg-[#111111] p-8 mb-10" data-testid="training-deck">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
                <PlayCircle size={40} className="text-[#FF6A00] flex-shrink-0"/>
                <div>
                  <div className="font-bebas text-2xl uppercase tracking-wide">Training Coming Soon</div>
                  <div className="text-sm text-[#A1A1AA]">The 30-minute training video is in production. Start with the free Fleet Crossroads Scorecard below.</div>
                </div>
              </div>
            </div>
          )}

          <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-3">What the training covers</div>
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {LEARN.map((l, i) => (
              <div key={l.t} className="border border-[#2B2B2B] bg-[#111111] p-5" data-testid={`training-learn-${i}`}>
                <div className="flex items-start gap-3">
                  <span className="chip-num flex-shrink-0">{String(i+1).padStart(2,"0")}</span>
                  <div>
                    <div className="font-bebas text-xl uppercase leading-tight mb-1">{l.t}</div>
                    <p className="text-sm text-[#A1A1AA] leading-relaxed">{l.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="border border-[#2B2B2B] bg-[#0A0A0A] p-5 mb-10" data-testid="training-trust">
            <p className="text-sm text-[#A1A1AA] leading-relaxed">
              This is education — not a promise of revenue, profit, savings, financing, or guaranteed business outcomes. Every fleet's facts are different. The purpose is to help you recognize patterns and determine whether deeper professional review is warranted.
            </p>
          </div>

          <div className="border border-[#FF6A00] bg-[#111111] p-6">
            <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">One dominant next step</div>
            <h2 className="font-bebas text-3xl uppercase mb-2">Take the Fleet Crossroads Scorecard.</h2>
            <p className="text-sm text-[#A1A1AA] mb-4">
              Score your fleet against the same five-area lens Brandon uses. The Scorecard leads to your personalized result and the correct next step.
            </p>
            <Link to="/fleet-crossroads-scorecard" className="btn-primary" data-testid="training-cta-assessment">
              Take the Fleet Crossroads Scorecard <ArrowRight size={20}/>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
